import { useState, useEffect, useCallback, useRef } from 'react';
import { useSnackbar } from 'notistack';
import {
  redeDiagramaService,
  RedeDiagramaConexao,
  RedeDiagramaNode,
  SavePosicaoPayload,
  CreateConexaoPayload,
  UpdateConexaoPortasPayload,
} from '../../../services/redeDiagramaService';
import { REDE_MONITOR_POLL_MS } from './monitoramentoRedeUtils';

export function useRedeDiagrama(enabled: boolean) {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [nodes, setNodes] = useState<RedeDiagramaNode[]>([]);
  const [conexoes, setConexoes] = useState<RedeDiagramaConexao[]>([]);
  const pollingRef = useRef(false);

  const loadDiagrama = useCallback(async (silent = false) => {

    if (silent && pollingRef.current) return;
    if (silent) pollingRef.current = true;

    try {
      if (!silent) setLoading(true);
      const data = await redeDiagramaService.getDiagrama();
      setNodes(data.nodes);
      setConexoes(data.conexoes);
    } catch {
      if (!silent) {
        enqueueSnackbar('Erro ao carregar diagrama de rede', { variant: 'error' });
        setNodes([]);
        setConexoes([]);
      }
    } finally {
      if (!silent) setLoading(false);
      if (silent) pollingRef.current = false;
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (!enabled) return undefined;

    void loadDiagrama();

    const intervalId = window.setInterval(() => {
      void loadDiagrama(true);
    }, REDE_MONITOR_POLL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [enabled, loadDiagrama]);

  const createConexao = useCallback(
    async (payload: CreateConexaoPayload) => {
      const conexao = await redeDiagramaService.createConexao(payload);
      setConexoes((prev) => [...prev, conexao]);
      enqueueSnackbar('Conexão criada', { variant: 'success' });
      return conexao;
    },
    [enqueueSnackbar]
  );

  const removeConexao = useCallback(
    async (id: number) => {
      await redeDiagramaService.removeConexao(id);
      setConexoes((prev) => prev.filter((c) => c.id !== id));
      enqueueSnackbar('Conexão removida', { variant: 'success' });
    },
    [enqueueSnackbar]
  );

  const savePosicoes = useCallback(async (payload: SavePosicaoPayload[]) => {
    await redeDiagramaService.savePosicoes(payload);
    setNodes((prev) =>
      prev.map((node) => {
        const update = payload.find(
          (p) => p.entidade_tipo === node.entidade_tipo && p.entidade_id === node.entidade_id
        );
        if (!update) return node;
        return { ...node, pos_x: update.pos_x, pos_y: update.pos_y };
      })
    );
  }, []);

  const updateConexaoPortas = useCallback(
    async (id: number, payload: UpdateConexaoPortasPayload) => {
      const conexao = await redeDiagramaService.updateConexaoPortas(id, payload);
      setConexoes((prev) => prev.map((item) => (item.id === id ? conexao : item)));
      return conexao;
    },
    []
  );

  return {
    loading,
    nodes,
    conexoes,
    loadDiagrama,
    createConexao,
    removeConexao,
    savePosicoes,
    updateConexaoPortas,
  };
}

export type UseRedeDiagramaReturn = ReturnType<typeof useRedeDiagrama>;
